import { Typography, Grid } from '@rbx/ui';
import React, { memo, useMemo } from 'react';
import StandardMarkdown from '@modules/ui/markdown/components/StandardMarkdown';
import CodeSamples from '@modules/ui/shared/components/CodeSamples';
import { useTranslation } from '@rbx/intl';
import { getShortMemberName } from '@modules/apiReference/page/utils/apiReferenceUtils';
import { APIReferenceMember, isMathOperationMember } from '@modules/client';
import { DatatypeCharacteristicRowTypes, DatatypeReferenceMathOperations } from '../../types';
import {
  isDatatypeProperty,
  isDatatypeFunction,
  isDatatypeConstructor,
  isDatatypeMethod,
  isDatatypeMathOperation,
} from '../../utils/characteristicsRowUtils';
import LinkableTypeReference from '../../sharedUI/components/LinkableTypeReference';
import ParameterTable from '../../sharedUI/components/ParameterTable';
import ReturnsTable from '../../sharedUI/components/ReturnsTable';
import CharacteristicsTableMathOperationRow from '../../class/components/CharacteristicsTableMathOperationRow';
import ReferenceMemberTags from '../../sharedUI/components/ReferenceMemberTags';
import { getVisibleReferenceTags } from '../../sharedUI/utils/tagUtils';
import {
  getMathOperationSlug,
  getUniqueMemberTitleHashId,
} from '../../utils/apiReferencePageNavUtils';

interface DatatypeCharacteristicsIteratorProps {
  rows: DatatypeCharacteristicRowTypes[];
}

interface DatatypeCharacteristicRowProps {
  row: DatatypeCharacteristicRowTypes;
  pageData?: { title: string };
  isSameNameAsPrevMember?: boolean;
}

const DatatypeCharacteristicRow = memo<DatatypeCharacteristicRowProps>(
  ({ row, pageData, isSameNameAsPrevMember }) => {
    const { translateHTML } = useTranslation();
    const isMathOperation = isDatatypeMathOperation(row);

    let type;
    const id = useMemo(
      () =>
        isMathOperation
          ? getMathOperationSlug(row as DatatypeReferenceMathOperations)
          : getUniqueMemberTitleHashId(
              // FIXME: DatatypeCharacteristicRowTypes needs to be replaced by
              // more generalized APIReferenceMember type
              row as unknown as APIReferenceMember,
              !!isSameNameAsPrevMember,
            ),
      [row, isSameNameAsPrevMember, isMathOperation],
    );

    const allTags = getVisibleReferenceTags({
      tags: row.tags,
    });

    const { description } = row;

    if (isDatatypeProperty(row)) {
      type = row.type;
    }
    if (isDatatypeFunction(row) || isDatatypeMethod(row)) {
      if (row.returns !== null) {
        type = row?.returns[0].type;
      }
    }

    const name = useMemo(() => {
      let nameVal: string;
      if (isMathOperation) {
        nameVal = (row as DatatypeReferenceMathOperations).operation;
      } else {
        nameVal = pageData?.title ?? getShortMemberName(row.name);
      }
      return nameVal;
    }, [row, pageData, isMathOperation]);

    if (isMathOperation) {
      return (
        <Grid item XSmall id={id}>
          <CharacteristicsTableMathOperationRow
            operation={row.operation}
            typeA={row.typeA}
            typeB={row.typeB}
            returnType={row.returnType}
            descriptions={row.description}
          />
        </Grid>
      );
    }

    return (
      <Grid item XSmall>
        <Grid container direction='column'>
          <Grid item XSmall>
            <Grid container alignItems='baseline' spacing={1}>
              <Grid item>
                <Typography
                  variant={pageData ? 'h1' : 'h3'}
                  component={pageData ? 'h1' : 'h3'}
                  id={id}>
                  {name}
                </Typography>
              </Grid>
              {!isDatatypeConstructor(row) && typeof type !== 'undefined' ? (
                <Grid item>
                  <LinkableTypeReference type={type} />
                </Grid>
              ) : null}
            </Grid>
          </Grid>
          <Grid container marginTop={1.5} marginBottom={2}>
            <Grid item>
              <ReferenceMemberTags tags={allTags} memberName={row.name} />
            </Grid>
          </Grid>
          {description && (
            <Grid item XSmall>
              <Typography variant='body1'>
                <StandardMarkdown>{description}</StandardMarkdown>
              </Typography>
            </Grid>
          )}
          {isDatatypeProperty(row) && row.default && (
            <Grid item XSmall>
              <Typography variant='largeLabel1'>
                {translateHTML('Description.DefaultValue', [
                  {
                    opening: 'defaultValueStart',
                    closing: 'defaultValueEnd',
                    // eslint-disable-next-line react/no-unstable-nested-components
                    content: () => {
                      return <Typography display='inline'>{row.default}</Typography>;
                    },
                  },
                ])}
              </Typography>
            </Grid>
          )}
          {!isDatatypeProperty(row) ? (
            <Grid item XSmall id={id}>
              <ParameterTable parameters={row.parameters} />
            </Grid>
          ) : null}
          {isDatatypeFunction(row) || isDatatypeMethod(row) ? (
            <Grid item XSmall>
              <ReturnsTable returnsList={row.returns} />
            </Grid>
          ) : null}
          {Array.isArray(row.codeSamples) && row.codeSamples.length > 0 ? (
            <Grid item XSmall>
              <CodeSamples codeSamples={row.codeSamples} />
            </Grid>
          ) : null}
        </Grid>
      </Grid>
    );
  },
);

DatatypeCharacteristicRow.displayName = 'DatatypeCharacteristicRow';

export const DatatypeCharacteristicsIterator: React.FC<
  React.PropsWithChildren<DatatypeCharacteristicsIteratorProps>
> = ({ rows }) => {
  return (
    <React.Fragment>
      {(rows as unknown as APIReferenceMember[]).map((member: APIReferenceMember, i) => {
        // TODO: clean up datatype rows type
        const prevMember = rows[i - 1] as unknown as APIReferenceMember;
        const isSameNameAsPrevMember =
          isMathOperationMember(member) || !prevMember || isMathOperationMember(prevMember)
            ? false
            : member.name === prevMember.name;
        return (
          <DatatypeCharacteristicRow
            // eslint-disable-next-line react/no-array-index-key
            key={`${member.name}-${i}`}
            row={member as unknown as DatatypeCharacteristicRowTypes}
            pageData={undefined}
            isSameNameAsPrevMember={isSameNameAsPrevMember}
          />
        );
      })}
    </React.Fragment>
  );
};

export default DatatypeCharacteristicsIterator;
